import { Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import {
	loadEntityTabRoute,
	validateEntityTabSearchOptional,
} from '@/entity-config-runtime'
import {
	getSettingsSectionKeyFromSlug,
	type SettingsSectionKey,
	settingsSectionKeyToSlug,
	settingsSectionLabelByKey,
} from '@/entity-config-runtime/entity-settings'
import { canReadEntityOrTab } from '@/integrations/casl/route-ability'
import { nav_settings } from '@/integrations/paraglide/messages/_index.js'
import { ScopedSettingsTab } from '../../../../../components/ScopedSettingsTab/ScopedSettingsTab'

export const Route = createFileRoute(
	'/_authenticated/$entity/$entityId/settings/$section',
)({
	beforeLoad: ({ params, context }) => {
		const entity = String(params.entity ?? '')
		if (!canReadEntityOrTab(context.ability, entity, 'settings')) {
			throw new Error(
				`Missing read permission for tab "settings" on entity "${entity}"`,
			)
		}
	},
	validateSearch: validateEntityTabSearchOptional,
	component: EntitySettingsSectionRoute,
	staticData: {
		breadcrumb: ({ params }) => {
			const sectionSlug = String(params.section ?? '')
			const sectionKey = getSettingsSectionKeyFromSlug(sectionSlug)
			if (!sectionKey) {
				return { label: nav_settings() }
			}
			return { label: settingsSectionLabelByKey[sectionKey] }
		},
	},
	loaderDeps({ search }) {
		return search
	},
	loader({ params, deps, context: { queryClient } }) {
		return loadEntityTabRoute({
			params: {
				entity: params.entity,
				entityId: params.entityId,
				tab: 'settings',
			},
			search: { ...deps, section: params.section },
			context: { queryClient },
		})
	},
})

function EntitySettingsSectionRoute() {
	const { entity, entityId, section } = Route.useParams()
	const navigate = Route.useNavigate()
	const sectionSlug = String(section)
	const sectionKey = getSettingsSectionKeyFromSlug(sectionSlug)

	if (!sectionKey) {
		return (
			<Typography color="text.secondary">Unknown settings section.</Typography>
		)
	}

	return (
		<ScopedSettingsTab
			entity={entity}
			entityId={entityId}
			section={sectionKey}
			onSectionChange={(nextSection: SettingsSectionKey) => {
				void navigate({
					to: '/$entity/$entityId/settings/$section',
					params: {
						entity,
						entityId,
						section: settingsSectionKeyToSlug[nextSection],
					},
					replace: true,
				})
			}}
		/>
	)
}
